import * as React from "react";
import { useMemo, useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { css } from "@emotion/core";
import { format } from "date-fns";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import { Spinner, NonIdealState, Tabs, Tab } from "@blueprintjs/core";
import { useMaterialsVolumes } from "../../../../hooks/useMaterialsVolumes";
import {
  MaterialVolume,
  MaterialProperty,
  LayerMaterials,
  PieRow,
} from "../../../../../../smokepit/src/types/MaterialVolumes";
import { useSinkState } from "../../../../context/sink";
import { SurveyWithExtras } from "../../../../interfaces";
import { unitConversionMethods } from "../../../../utils/convert";
import { TAG_COLORS } from "../../../common/tagEditorMachine";
import { PieTooltip } from "./PieTooltip";
import { LineTooltip } from "./LineTooltip";
import { PieLabel } from "./PieLabel";
import { XAxisTick } from "./XAxisTick";
import { YAxisTick } from "./YAxisTick";
import { LineLabel } from "./LineLabel";
import {
  buildMaterialColorMap,
  findMaterialInSurveys,
} from "./materialsVolumesChartUtils";
import { useUnitState } from "../../../../context";

// TAG_COLORS darkened by 20% for chart use
const MATERIAL_COLORS = [
  "#BF3636", // #EF4444 darkened
  "#C75C12", // #F97316 darkened
  "#C47E09", // #F59E0B darkened
  "#BB8F06", // #EAB308 darkened
  "#6AA312", // #84CC16 darkened
  "#1B9E4B", // #22C55E darkened
  "#0D9467", // #10B981 darkened
  "#109385", // #14B8A6 darkened
  "#0592AA", // #06B6D4 darkened
  "#0B84BA", // #0EA5E9 darkened
  "#2F68C5", // #3B82F6 darkened
  "#4F52C1", // #6366F1 darkened
  "#6F4AC5", // #8B5CF6 darkened
  "#8644C6", // #A855F7 darkened
  "#AE38BF", // #D946EF darkened
  "#BD3A7A", // #EC4899 darkened
  "#C3324B", // #F43F5E darkened
];

interface MaterialsVolumesChartsProps {
  surveyId: number;
  currentSurveyId: number;
  siteId: number | null;
}

const formatUnitDisplay = (unit: string) =>
  unit.replace(/\^2/g, "²").replace(/\^3/g, "³");

interface CustomDotProps {
  cx?: number;
  cy?: number;
  payload?: { date?: string; [key: string]: any };
  fill?: string;
  isHovered?: boolean;
  hasHover?: boolean;
  stroke?: string;
  dateToSurveyMap?: Map<string, number>;
  onDateClick?: (surveyId: number) => void;
  onDateHover?: (dateStr: string | null) => void;
}

const CustomDot: React.FC<CustomDotProps> = ({
  cx,
  cy,
  payload,
  fill,
  isHovered,
  hasHover,
  stroke,
  dateToSurveyMap,
  onDateClick,
  onDateHover,
}) => {
  const dateStr = payload?.date;
  const surveyId =
    dateStr && dateToSurveyMap ? dateToSurveyMap.get(dateStr) : undefined;

  if (cx == null || cy == null) return null;

  const handleMouseEnter = () => {
    if (dateStr && onDateHover) {
      onDateHover(dateStr);
    }
  };

  const handleMouseLeave = () => {
    if (onDateHover) {
      onDateHover(null);
    }
  };

  return (
    <circle
      cx={cx}
      cy={cy}
      r={6}
      fill={fill || stroke}
      stroke={stroke}
      strokeWidth={isHovered ? 6 : 4}
      opacity={!hasHover || isHovered ? 1 : 0.25}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{ cursor: surveyId ? "pointer" : "default" }}
    />
  );
};

export const MaterialsVolumesCharts: React.FC<MaterialsVolumesChartsProps> = ({
  surveyId,
  currentSurveyId,
  siteId,
}) => {
  const navigate = useNavigate();
  const { data, isLoading, isError } = useMaterialsVolumes(surveyId);
  const { surveys, sink } = useSinkState();
  const [selectedLayerLabel, setSelectedLayerLabel] = useState<string | null>(
    null
  );
  const [hoveredMaterial, setHoveredMaterial] = useState<{
    materialId: number;
    section: string;
  } | null>(null);
  const [hoveredDateString, setHoveredDateString] = useState<string | null>(
    null
  );

  const { unit: orgUnits } = useUnitState();

  /** Raw totals from the API are in metric units; this converts (if org is SAE) then formats. */
  const convertAndFormatPropertyValue = useCallback(
    (volume: number, prop: MaterialProperty): string => {
      if (orgUnits === "sae") {
        const conversionMethod = unitConversionMethods[prop.metric_unit];
        if (conversionMethod) {
          const converted = conversionMethod(volume, prop.imperial_precision);
          return `${converted.toLocaleString()} ${formatUnitDisplay(
            prop.imperial_unit
          )}`;
        }
        return `${volume.toLocaleString()} ${formatUnitDisplay(
          prop.imperial_unit
        )}`;
      }

      return `${volume.toLocaleString("en", {
        minimumFractionDigits: prop.metric_precision,
        maximumFractionDigits: prop.metric_precision,
      })} ${formatUnitDisplay(prop.metric_unit)}`;
    },
    [orgUnits]
  );

  /** Line chart points are pre-converted to display units in lineData; only precision + unit label are applied. */
  const formatAlreadyConvertedPropertyValue = useCallback(
    (volume: number, prop: MaterialProperty): string => {
      const unitLabel =
        orgUnits === "sae"
          ? formatUnitDisplay(prop.imperial_unit)
          : formatUnitDisplay(prop.metric_unit);
      const precision =
        orgUnits === "sae"
          ? prop.imperial_precision
          : prop.metric_precision;
      return `${volume.toLocaleString("en", {
        minimumFractionDigits: precision,
        maximumFractionDigits: precision,
      })} ${unitLabel}`;
    },
    [orgUnits]
  );

  const siteSurveys = useMemo(() => {
    if (!siteId) return [];
    return (surveys || []).filter(
      (s: SurveyWithExtras) => (s as any).site_id === siteId
    );
  }, [surveys, siteId]);

  const currentSurveyData = useMemo(() => {
    if (!data) return null;
    if (data.current_survey) {
      return data.current_survey;
    }
    if (data.site_surveys) {
      return (
        data.site_surveys.find((s) => s.survey_id === currentSurveyId) || null
      );
    }
    if ((data as any).layers) {
      return data as any;
    }
    return null;
  }, [data, currentSurveyId]);

  const layers = useMemo(() => {
    if (!currentSurveyData?.layers) return [];
    return [...currentSurveyData.layers].sort((a, b) =>
      a.layer_label.localeCompare(b.layer_label)
    );
  }, [currentSurveyData]);

  useEffect(() => {
    if (layers.length > 0 && selectedLayerLabel === null) {
      setSelectedLayerLabel(layers[0].layer_label);
    }
  }, [layers, selectedLayerLabel]);

  const filteredMaterials = useMemo(() => {
    const layer = layers.find(
      (l: LayerMaterials) => l.layer_label === selectedLayerLabel
    );
    return layer?.materials || [];
  }, [layers, selectedLayerLabel]);

  const allSiteSurveysData = useMemo(() => {
    if (!data) return [];
    if (data.site_surveys) {
      return data.site_surveys;
    }
    if (currentSurveyData) {
      return [currentSurveyData];
    }
    return [];
  }, [data, currentSurveyData]);

  const chartSections = useMemo(() => {
    if (allSiteSurveysData.length === 0 || selectedLayerLabel === null) {
      return [];
    }

    const propertyLabelsOrdered: string[] = [];
    const seen = new Set<string>();
    for (const m of filteredMaterials) {
      for (const p of m.properties || []) {
        if (!seen.has(p.property_label)) {
          seen.add(p.property_label);
          propertyLabelsOrdered.push(p.property_label);
        }
      }
    }

    const getMaterialsForSelectedLayer = (survey: any): MaterialVolume[] => {
      if (!survey.layers) return [];
      const layer = survey.layers.find(
        (layer: LayerMaterials) => layer.layer_label === selectedLayerLabel
      );
      return layer?.materials || [];
    };

    const sortedSurveys = [...allSiteSurveysData].sort((a, b) => {
      const dateA = a.nominal_flight_date
        ? new Date(a.nominal_flight_date).getTime()
        : Infinity;
      const dateB = b.nominal_flight_date
        ? new Date(b.nominal_flight_date).getTime()
        : Infinity;
      return dateA - dateB;
    });

    return propertyLabelsOrdered.map((propertyLabel) => {
      const pieData: PieRow[] = filteredMaterials.flatMap(
        (m: MaterialVolume) => {
          const materialProperty = m.properties?.find(
            (p) => p.property_label === propertyLabel
          );
          if (!materialProperty) {
            return [];
          }
          return [
            {
              name: m.name,
              material_id: m.material_id,
              value: materialProperty.total_value ?? 0,
              material: m,
              materialProperty,
            },
          ];
        }
      );

      const allMaterialIds = new Set<number>();
      sortedSurveys.forEach((survey) => {
        getMaterialsForSelectedLayer(survey).forEach((m: MaterialVolume) => {
          if (
            m.properties?.some((p) => p.property_label === propertyLabel)
          ) {
            allMaterialIds.add(m.material_id);
          }
        });
      });

      const lineData = sortedSurveys.map((survey) => {
        const dateStr = survey.nominal_flight_date
          ? format(new Date(survey.nominal_flight_date), "MMM d, yyyy")
          : "Unknown";
        const entry: Record<string, string | number> = { date: dateStr };
        const materials = getMaterialsForSelectedLayer(survey);
        materials.forEach((m: MaterialVolume) => {
          const prop = m.properties?.find(
            (p) => p.property_label === propertyLabel
          );
          if (!prop) {
            return;
          }
          const dataKey = `material_${m.material_id}`;
          if (orgUnits === "sae") {
            const conversionMethod = unitConversionMethods[prop.metric_unit];
            if (conversionMethod) {
              entry[dataKey] = conversionMethod(
                prop.total_value,
                prop.imperial_precision
              );
            } else {
              entry[dataKey] = prop.total_value;
            }
          } else {
            entry[dataKey] = prop.total_value;
          }
        });
        allMaterialIds.forEach((materialId) => {
          const dataKey = `material_${materialId}`;
          if (!(dataKey in entry)) {
            entry[dataKey] = 0;
          }
        });
        return entry;
      });

      const materialIds = Array.from(allMaterialIds);
      const firstProp = pieData[0]?.materialProperty;
      const yAxisUnit = firstProp
        ? formatUnitDisplay(
            orgUnits === "sae"
              ? firstProp.imperial_unit
              : firstProp.metric_unit
          )
        : "";

      return {
        propertyLabel,
        pieData,
        lineData,
        materialIds,
        yAxisUnit,
      };
    });
  }, [allSiteSurveysData, filteredMaterials, selectedLayerLabel, orgUnits]);

  const currentSurveyDateString = useMemo(() => {
    if (!allSiteSurveysData || allSiteSurveysData.length === 0) return null;
    const currentSurvey = allSiteSurveysData.find(
      (s) => s.survey_id === currentSurveyId
    );
    if (!currentSurvey || !currentSurvey.nominal_flight_date) return null;
    return format(new Date(currentSurvey.nominal_flight_date), "MMM d, yyyy");
  }, [allSiteSurveysData, currentSurveyId]);

  const dateToSurveyMap = useMemo(() => {
    const map = new Map<string, number>();
    allSiteSurveysData.forEach((survey) => {
      if (survey.nominal_flight_date) {
        const dateStr = format(
          new Date(survey.nominal_flight_date),
          "MMM d, yyyy"
        );
        map.set(dateStr, survey.survey_id);
      }
    });
    return map;
  }, [allSiteSurveysData]);

  const handleDateClick = (surveyId: number) => {
    navigate(`/surveys/${surveyId}`);
  };

  const handleChartClick = () => {
    if (hoveredDateString) {
      const surveyId = dateToSurveyMap.get(hoveredDateString);
      if (surveyId) {
        navigate(`/surveys/${surveyId}`);
      }
    }
  };

  const materialColorMap = useMemo(
    () =>
      buildMaterialColorMap(
        filteredMaterials,
        allSiteSurveysData,
        selectedLayerLabel,
        MATERIAL_COLORS
      ),
    [filteredMaterials, allSiteSurveysData, selectedLayerLabel]
  );

  const brightMaterialColorMap = useMemo(
    () =>
      buildMaterialColorMap(
        filteredMaterials,
        allSiteSurveysData,
        selectedLayerLabel,
        TAG_COLORS
      ),
    [filteredMaterials, allSiteSurveysData, selectedLayerLabel]
  );

  const hasMultipleSurveys = siteSurveys.length > 1;
  const hasAnyChartData = chartSections.some((s) => s.pieData.length > 0);

  if (isLoading) {
    return (
      <div
        css={css`
          display: flex;
          justify-content: center;
          align-items: center;
          padding: 2rem;
          min-height: 300px;
        `}
      >
        <Spinner />
      </div>
    );
  }

  if (isError || layers.length === 0 || !hasAnyChartData) {
    return (
      <div
        css={css`
          padding: 0 0 2rem 0;
          min-height: 120px;
        `}
      >
        <NonIdealState
          icon="chart"
          title="No Volume Data Available"
          description="Material volume data is not available for this survey."
        />
      </div>
    );
  }

  return (
    <div
      css={css`
        padding: 0 1.5rem 1.5rem 1.5rem;
        min-height: 400px;
      `}
    >
      {layers.length > 0 && (
        <div
          css={css`
            margin-bottom: 1.5rem;
          `}
        >
          <Tabs
            id="layer-tabs"
            selectedTabId={selectedLayerLabel}
            onChange={(id) => setSelectedLayerLabel(id as string)}
          >
            {layers.map((layer: LayerMaterials) => (
              <Tab
                key={layer.layer_label}
                id={layer.layer_label}
                title={layer.layer_label}
              />
            ))}
          </Tabs>
        </div>
      )}

      {chartSections.map((section) => {
        if (section.pieData.length === 0) {
          return null;
        }
        const hasLineData =
          hasMultipleSurveys &&
          section.lineData.length > 1 &&
          section.materialIds.length > 0;
        const total = section.pieData.reduce(
          (sum: number, item: PieRow) => sum + item.value,
          0
        );
        const formatProp = section.pieData[0].materialProperty;
        const sectionHovered = hoveredMaterial?.section === section.propertyLabel;

        return (
          <div
            key={section.propertyLabel}
            css={css`
              margin-bottom: 2.5rem;

              &:last-of-type {
                margin-bottom: 0;
              }
            `}
          >
            <h3
              css={css`
                margin: 0 0 0.75rem 0;
                font-size: 1.05rem;
                font-weight: 600;
                color: var(--neutral-1);
              `}
            >
              {section.propertyLabel}
            </h3>
            <div
              css={css`
                display: flex;
                gap: 2rem;
                @media (max-width: 1560px) {
                  flex-direction: column;
                }

                & svg:focus,
                & g:focus,
                & path:focus {
                  outline-color: transparent;
                }
              `}
            >
              <div
                css={css`
                  flex: 1;
                  min-width: 0;
                `}
              >
                <div
                  css={css`
                    text-align: center;
                    font-weight: 600;
                    font-size: 1rem;
                    color: var(--neutral-1);
                    margin-bottom: 0.5rem;
                  `}
                >
                  <span
                    css={css`
                      font-weight: 400;
                      color: var(--neutral-3);
                    `}
                  >
                    Total:{" "}
                  </span>
                  {convertAndFormatPropertyValue(total, formatProp)}
                </div>
                <ResponsiveContainer width="100%" height={360}>
                  <PieChart>
                    <Pie
                      data={section.pieData}
                      cx="50%"
                      cy="50%"
                      outerRadius="70%"
                      dataKey="value"
                      labelLine={true}
                      label={(props) => (
                        <PieLabel
                          {...props}
                          pieData={section.pieData}
                          convertAndFormatPropertyValue={
                            convertAndFormatPropertyValue
                          }
                        />
                      )}
                      isAnimationActive={false}
                      stroke={
                        sectionHovered
                          ? "var(--neutral-1)"
                          : "var(--neutral-2)"
                      }
                      strokeWidth={sectionHovered ? 1 : 2}
                    >
                      {section.pieData.map((entry: PieRow) => {
                        const isHovered =
                          sectionHovered &&
                          hoveredMaterial?.materialId === entry.material_id;
                        const hasHover = sectionHovered;
                        const opacity = !hasHover || isHovered ? 1 : 0.3;
                        return (
                          <Cell
                            key={`cell-${section.propertyLabel}-${entry.material_id}`}
                            fill={
                              materialColorMap.get(entry.material_id) ||
                              MATERIAL_COLORS[0]
                            }
                            style={{ opacity }}
                            onMouseEnter={() =>
                              setHoveredMaterial({
                                materialId: entry.material_id,
                                section: section.propertyLabel,
                              })
                            }
                            onMouseLeave={() => setHoveredMaterial(null)}
                          />
                        );
                      })}
                    </Pie>
                    <Tooltip
                      content={
                        <PieTooltip
                          propertyLabel={section.propertyLabel}
                          pieData={section.pieData}
                          convertAndFormatPropertyValue={
                            convertAndFormatPropertyValue
                          }
                          brightMaterialColorMap={brightMaterialColorMap}
                        />
                      }
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              {hasLineData && (
                <div
                  css={css`
                    flex: 1;
                    min-width: 0;

                    & .recharts-surface {
                      cursor: pointer;
                    }
                  `}
                >
                  <div
                    css={css`
                      text-align: center;
                      font-weight: 600;
                      font-size: 1rem;
                      color: ${
                      sectionHovered && hoveredMaterial
                        ? brightMaterialColorMap.get(hoveredMaterial.materialId) ||
                          "var(--neutral-2)"
                        : "var(--neutral-2)"
                    };
                      margin-bottom: 0.5rem;
                      opacity: ${sectionHovered ? 1 : 0};
                    `}
                  >
                    {sectionHovered && hoveredMaterial
                      ? findMaterialInSurveys(
                          allSiteSurveysData,
                          selectedLayerLabel,
                          hoveredMaterial.materialId
                        )?.name || "Unknown"
                      : "Hover over a material to see its value"}
                  </div>
                  <ResponsiveContainer width="100%" height={360}>
                    <LineChart
                      data={section.lineData}
                      margin={{ top: 10, bottom: 60, right: 60 }}
                      onClick={handleChartClick}
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="var(--neutral-5)"
                      />
                      <XAxis
                        dataKey="date"
                        stroke="var(--neutral-3)"
                        style={{ fontSize: "0.75rem" }}
                        tick={(props) => (
                          <XAxisTick
                            {...props}
                            currentSurveyDateString={currentSurveyDateString}
                            surveyId={dateToSurveyMap.get(
                              props.payload?.value
                            )}
                            onDateClick={handleDateClick}
                            hoveredDateString={hoveredDateString}
                            onDateHover={setHoveredDateString}
                          />
                        )}
                      />
                      <YAxis
                        stroke={
                          sectionHovered
                            ? "var(--neutral-5)"
                            : "var(--neutral-3)"
                        }
                        style={{ fontSize: "0.75rem" }}
                        tick={(props) => (
                          <YAxisTick
                            {...props}
                            hoveredMaterial={
                              sectionHovered && hoveredMaterial
                                ? hoveredMaterial.materialId
                                : null
                            }
                          />
                        )}
                        tickFormatter={(value) => {
                          let formattedValue: string;
                          if (value >= 1000000) {
                            const millions = value / 1000000;
                            formattedValue = `${millions.toLocaleString("en", {
                              minimumFractionDigits: 1,
                              maximumFractionDigits: 1,
                            })} M`;
                          } else if (value >= 1000) {
                            const thousands = value / 1000;
                            formattedValue = `${thousands.toLocaleString("en", {
                              minimumFractionDigits: 1,
                              maximumFractionDigits: 1,
                            })} k`;
                          } else {
                            formattedValue = value.toLocaleString("en");
                          }

                          return section.yAxisUnit
                            ? `${formattedValue} ${section.yAxisUnit}`
                            : formattedValue;
                        }}
                      />
                      <Tooltip
                        content={
                          <LineTooltip
                            propertyLabel={section.propertyLabel}
                            allSiteSurveysData={allSiteSurveysData}
                            selectedLayerLabel={selectedLayerLabel}
                            formatAlreadyConvertedPropertyValue={
                              formatAlreadyConvertedPropertyValue
                            }
                            brightMaterialColorMap={brightMaterialColorMap}
                            onDateHover={setHoveredDateString}
                          />
                        }
                      />
                      {section.materialIds.map((materialId) => {
                        const dataKey = `material_${materialId}`;
                        const material = findMaterialInSurveys(
                          allSiteSurveysData,
                          selectedLayerLabel,
                          materialId
                        );
                        const materialName = material?.name || "Unknown";
                        const color =
                          materialColorMap.get(materialId) ||
                          MATERIAL_COLORS[0];
                        const isHovered =
                          sectionHovered &&
                          hoveredMaterial?.materialId === materialId;
                        const hasHover = sectionHovered;
                        return (
                          <Line
                            key={`${section.propertyLabel}-${materialId}`}
                            type="linear"
                            dataKey={dataKey}
                            stroke={color}
                            strokeWidth={isHovered ? 6 : 4}
                            opacity={!hasHover || isHovered ? 1 : 0.25}
                            dot={
                              <CustomDot
                                dateToSurveyMap={dateToSurveyMap}
                                isHovered={isHovered}
                                hasHover={hasHover}
                                onDateClick={handleDateClick}
                                onDateHover={setHoveredDateString}
                              />
                            }
                            activeDot={
                              <CustomDot
                                dateToSurveyMap={dateToSurveyMap}
                                isHovered={isHovered}
                                hasHover={hasHover}
                                onDateClick={handleDateClick}
                                onDateHover={setHoveredDateString}
                              />
                            }
                            name={materialName}
                            isAnimationActive={false}
                            label={(props: any) => (
                              <LineLabel
                                {...props}
                                materialId={materialId}
                                propertyLabel={section.propertyLabel}
                                hoveredMaterial={
                                  sectionHovered
                                    ? hoveredMaterial?.materialId ?? null
                                    : null
                                }
                                allSiteSurveysData={allSiteSurveysData}
                                selectedLayerLabel={selectedLayerLabel}
                                formatAlreadyConvertedPropertyValue={
                                  formatAlreadyConvertedPropertyValue
                                }
                                currentSurveyDateString={
                                  currentSurveyDateString
                                }
                                lineData={section.lineData}
                              />
                            )}
                          />
                        );
                      })}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};
